import { useEffect, useState } from "react";
import { Loader2, Save, Undo2, FileJson, History } from "lucide-react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

/* AdminCmsTab
 * -----------
 * Minimal "Phase 1" CMS — section picker + JSON editor + audit log. Hosts the
 * dropdown of every known section (defaults + saved), loads the merged content
 * for editing, lets the admin save (PUT) or reset to default. The editor
 * intentionally uses raw JSON so we can ship today; structured per-field forms
 * arrive in Phase 2 once schemas stabilize.
 */

export default function AdminCmsTab() {
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState("homepage");
  const [text, setText] = useState("");
  const [original, setOriginal] = useState("");
  const [parseError, setParseError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState([]);

  // Initial section list
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/cms/sections")
        .then(({ data }) => setSections(data.sections || []))
        .catch(() => { /* silent */ });
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // Load active section content + audit log
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      setLoading(true);
      Promise.all([
        api.get(`/cms/content/${active}`),
        api.get(`/admin/cms/audit-log?section=${active}&limit=15`),
      ]).then(([c, l]) => {
        if (cancelled) return;
        const pretty = JSON.stringify(c.data.data || {}, null, 2);
        setText(pretty);
        setOriginal(pretty);
        setLog(l.data.items || []);
      }).catch(() => { if (!cancelled) toast.error("No pudimos cargar la sección"); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [active]);

  const dirty = text !== original;
  const handleSave = async () => {
    let parsed;
    try {
      parsed = JSON.parse(text);
      if (typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("Debe ser un objeto JSON.");
      setParseError(null);
    } catch (e) {
      setParseError(e.message);
      toast.error("JSON inválido — revisa el formato");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/cms/content/${active}`, { data: parsed });
      toast.success(`Guardado · v${data.version}`);
      setOriginal(text);
      // Refresh audit log
      const l = await api.get(`/admin/cms/audit-log?section=${active}&limit=15`);
      setLog(l.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setText(original);
    setParseError(null);
  };

  return (
    <div className="space-y-6" data-testid="admin-cms-tab">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-agave">Contenido del sitio</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Edita textos, imágenes y secciones de la web. Los cambios aparecen al instante en rivones.com — no requiere redeploy.
        </p>
      </div>

      <div className="grid lg:grid-cols-[200px_1fr] gap-5">
        {/* Section nav */}
        <div className="card-base p-2 self-start lg:sticky lg:top-4">
          <ul className="space-y-0.5">
            {sections.map((s) => (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => setActive(s.key)}
                  data-testid={`cms-section-${s.key}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                    active === s.key
                      ? "bg-terracotta text-white font-medium"
                      : "hover:bg-neutral-100 text-neutral-700"
                  }`}
                >
                  {s.key}
                  {s.saved ? <span className="ml-1 text-[10px] opacity-70">v{s.saved.version}</span> : null}
                </button>
              </li>
            ))}
            {sections.length === 0 && (
              <li className="px-3 py-2 text-sm text-neutral-500">Cargando…</li>
            )}
          </ul>
        </div>

        {/* Editor */}
        <div className="space-y-4 min-w-0">
          <div className="card-base">
            <div className="flex items-center gap-2 mb-3">
              <FileJson className="w-4 h-4 text-terracotta" />
              <h3 className="font-semibold text-agave">{active}</h3>
              {dirty && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium">
                  Sin guardar
                </span>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
              </div>
            ) : (
              <>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={20}
                  spellCheck={false}
                  data-testid="cms-editor"
                  className="font-mono text-[13px] leading-snug bg-neutral-50"
                />
                {parseError && (
                  <p className="mt-2 text-xs text-rose-700">JSON inválido: {parseError}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    disabled={!dirty || saving}
                    data-testid="cms-reset-btn"
                  >
                    <Undo2 className="w-4 h-4 mr-2" />
                    Descartar cambios
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    data-testid="cms-save-btn"
                    className="bg-terracotta hover:bg-terracotta/90"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Publicar
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Audit log */}
          <div className="card-base">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-neutral-500" />
              <h3 className="font-semibold text-agave text-sm">Historial</h3>
            </div>
            {log.length === 0 ? (
              <p className="text-xs text-neutral-500">Sin cambios registrados.</p>
            ) : (
              <ul className="divide-y divide-neutral-100 text-xs">
                {log.map((r, i) => (
                  <li key={i} className="py-2 flex justify-between gap-3">
                    <span className="font-mono text-neutral-500">v{r.version_before}→v{r.version_after}</span>
                    <span className="text-neutral-700 truncate">{r.changed_by}</span>
                    <span className="text-neutral-500 shrink-0">{new Date(r.changed_at).toLocaleString("es-MX")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
